import {fetchJSONMemoized} from '@gaia/utils/fetchJSON';
import memoize from 'lodash/memoize';
import sortBy from 'lodash/sortBy';

export interface DefaultSources {
    sources: string[];
    huntingStates: HuntingStates[];
}

export interface HuntingStates {
    state: string;
    country: string | undefined;
    sources: string[];
}

interface fetchDefaultUserLayersArgs {
    hasSubscription: boolean;
    canAccessNonPublicSources: boolean;
}

// exported for Jest tests
// @TODO: desperately needs to be typed. `fetchDefaultUserLayers` ends up with lots of `any`s.
export const getMapSources = memoize(async function getMapSources(
    canAccessNonPublicSources: boolean
) {
    const url = `/mapsources/v2/gaiagps-ng-2019/?nonPublicSources=${canAccessNonPublicSources}`;
    const mapsources = await fetchJSONMemoized(url, {
        credentials: 'same-origin',
    });
    return mapsources;
});

// Get default layers to display in users layers menu and on the map
export const fetchDefaultUserLayers = memoize(
    async function fetchDefaultUserLayers({
        hasSubscription,
        canAccessNonPublicSources,
    }: fetchDefaultUserLayersArgs): Promise<UserLayer[] | undefined> {
        const mapsources = await getMapSources(canAccessNonPublicSources);
        const language = (window.navigator as any).userLanguage || window.navigator.language;
        const languageKey = language === 'en-US' ? 'US' : 'default';
        const membershipKey = hasSubscription ? 'pro_sources' : 'sources';

        const defaultType = mapsources.defaults[languageKey].default_source;
        const types = [
            ...new Set([defaultType, ...mapsources.defaults[languageKey][membershipKey]]),
        ];

        const defaultUserLayers = sortBy(
            types.map((type: string) => ({
                isVisible: defaultType === type,
                layer: {
                    type,
                    opacity: 1,
                },
            })),
            'isVisible'
        );

        if (!defaultUserLayers.find(({isVisible}) => isVisible)) {
            console.error('No default user layer is visible.');
            defaultUserLayers[0].isVisible = true;
        }

        return defaultUserLayers;
    },
    (argsObj: fetchDefaultUserLayersArgs) => JSON.stringify(argsObj)
);

// Get recommended default layers for activities
export const fetchDefaultLayers = memoize(
    async (
        canAccessNonPublicSources: boolean,
        activitiesList: string[]
    ): Promise<DefaultSources> => {
        const defaultSources: DefaultSources = {
            sources: [],
            huntingStates: [],
        };
        if (activitiesList.length === 0) return defaultSources;
        const mapsources = await getMapSources(canAccessNonPublicSources);
        if (!mapsources) return defaultSources;
        const language = (window.navigator as any).userLanguage || window.navigator.language;
        const languageKey = language === 'en-US' ? 'US' : language === 'en-CA' ? 'CA' : '';

        const types = (activity: string) => {
            if (activity === 'snow') {
                activity = 'backcountry-ski';
            } else if (activity === 'offroad') {
                activity = 'overland-camping';
            } // match id with available activity choices in DefaultMapSourceGroup
            const defaultActivity = mapsources.defaults_activity[activity];
            if (!defaultActivity) return;
            defaultActivity.forEach((layersDefault: LayerDefaultsObject) => {
                if (!layersDefault.state) {
                    defaultSources.sources.push(...layersDefault.pro_sources);
                    defaultSources.sources.push(...layersDefault.sources);
                } else if (layersDefault.activity === 'hunting') {
                    if (languageKey && languageKey !== layersDefault.country) return;
                    defaultSources.huntingStates.push({
                        state: layersDefault.state,
                        country: layersDefault.country,
                        sources: layersDefault.pro_sources,
                    });
                }
            });
        };

        activitiesList.forEach((activity) => types(activity));
        defaultSources.sources = [...new Set(defaultSources.sources)];
        return defaultSources;
    },
    (...args) => JSON.stringify(args)
);
